import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ObraProperties, WandaProject } from '../types/ObraDetails';
import ObrasAPI from '../services/APIObras';

interface DetailsViewContextValues {
  obra?: ObraProperties,
  wandaProject?: WandaProject,
  wandaProjectLoading: boolean,
  wandaProjectError: boolean,
  loadedDetailSections: Record<string, boolean>,
  markDetailSectionLoaded: (sectionKey: string) => void,
  clearLoadedDetailSections: () => void,
  mapIsSelected: boolean,
  handleVisualization: (visible: boolean) => void,
  setObra: React.Dispatch<React.SetStateAction<ObraProperties | undefined>>
}

const defaultViewContextValues: DetailsViewContextValues = {
  wandaProjectLoading: false,
  wandaProjectError: false,
  loadedDetailSections: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  markDetailSectionLoaded: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  clearLoadedDetailSections: () => {},
  mapIsSelected: true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleVisualization: () => { },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setObra: () => {},
};

export const DetailsViewContext = createContext<DetailsViewContextValues>(
  defaultViewContextValues
);


interface DetailsViewContextProviderProps {
  /**
   * The elements wrapped by the auth context.
   */
  children: JSX.Element;
}
const DetailsViewProvider = (props: DetailsViewContextProviderProps) => {
  const { codCentro } = useParams<string>();
  const [mapIsSelected, setMapIsSelected] = useState<boolean>(true);
  const [obra, setObra] = useState<ObraProperties>();
  const [wandaProject, setWandaProject] = useState<WandaProject>();
  const [wandaProjectLoading, setWandaProjectLoading] = useState(false);
  const [wandaProjectError, setWandaProjectError] = useState(false);
  const [loadedDetailSections, setLoadedDetailSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;
    setWandaProject(undefined);
    setObra(undefined);
    setWandaProjectError(false);
    setLoadedDetailSections({});

    if (!codCentro) return () => {
      active = false;
    };

    setWandaProjectLoading(true);
    ObrasAPI.getInstance().getWandaProjectAPI(codCentro)
      .then((response) => {
        if (active) setWandaProject(response);
      })
      .catch(() => {
        if (active) setWandaProjectError(true);
      })
      .finally(() => {
        if (active) setWandaProjectLoading(false);
      });

    return () => {
      active = false;
    };
  }, [codCentro]);

  const handleVisualization = (visible: boolean) => {
    setMapIsSelected(visible);
  };

  const markDetailSectionLoaded = useCallback((sectionKey: string) => {
    setLoadedDetailSections((currentSections) => ({
      ...currentSections,
      [sectionKey]: true,
    }));
  }, []);

  const clearLoadedDetailSections = useCallback(() => {
    setLoadedDetailSections({});
  }, []);
  // Setup the context provider
  return (
    <DetailsViewContext.Provider
      value={
        {
          obra,
          setObra,
          wandaProject,
          wandaProjectLoading,
          wandaProjectError,
          loadedDetailSections,
          markDetailSectionLoaded,
          clearLoadedDetailSections,
          mapIsSelected,
          handleVisualization,
        }}
    >
      {props.children}
    </DetailsViewContext.Provider>
  );
};

export default DetailsViewProvider;
